class FacetFiltersForm extends HTMLElement {
	constructor() {
		super();
		this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

		this.debouncedOnSubmit = debounce((event) => {
			this.onSubmitHandler(event);
		}, 200);

		this.facetForm = this.querySelector("form");
		// Sync duplicate checkboxes (desktop ↔ drawer share same name/value).
		// Must run synchronously so the debounced submit reads correct state.
		this.facetForm?.addEventListener("input", (event) => {
			const input = event.target;
			if (input.type !== "checkbox") {
				FacetFiltersForm.beginInteraction();
				return;
			}
			FacetFiltersForm.startTrace("checkbox", input.name);
			this.facetForm.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
				if (cb !== input && cb.name === input.name && cb.value === input.value) {
					cb.checked = input.checked;
				}
			});
		});
		this.facetForm?.addEventListener(
			"input",
			this.debouncedOnSubmit.bind(this)
		);

		const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
		if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);

		this.bindFacetTriggers();

		document.addEventListener("click", (e) => {
			const detailsList = document.querySelectorAll(
				".facets__wrapper--horizontal .facets__disclosure, .facets__desktop-filters .facets__disclosure--desktop"
			);
			const details = e.target.closest(".facets__disclosure[open]");

			if (!details && detailsList) {
				detailsList.forEach((item) => {
					item.removeAttribute("open");
				});
			}
		});

		if (this.querySelector(".facet-sort-select")) {
			this.initCustomSortSelect();
		}
	}

	bindFacetTriggers() {
		const facetTrigger = this.querySelector(".facets__button-show");
		const closeButtons = this.querySelectorAll(
			".close-drawer, .facets__container__overlay"
		);

		// Drawer tab switching
		const drawerTabs = this.querySelectorAll(".facets__drawer-tab");
		const drawerPanels = this.querySelectorAll(".facets__drawer-panel");
		drawerTabs.forEach((tab) => {
			tab.addEventListener("click", () => {
				drawerTabs.forEach((t) => t.classList.remove("facets__drawer-tab--active"));
				drawerPanels.forEach((p) => p.classList.remove("facets__drawer-panel--active"));
				tab.classList.add("facets__drawer-tab--active");
				const target = this.querySelector(`.facets__drawer-panel[data-panel="${tab.dataset.tab}"]`);
				if (target) target.classList.add("facets__drawer-panel--active");
			});
		});

		// After re-render, sync tab highlight to whichever panel is currently active
		const activePanel = this.querySelector(".facets__drawer-panel.facets__drawer-panel--active");
		if (activePanel) {
			drawerTabs.forEach((t) => t.classList.remove("facets__drawer-tab--active"));
			const matchingTab = this.querySelector(`.facets__drawer-tab[data-tab="${activePanel.dataset.panel}"]`);
			if (matchingTab) matchingTab.classList.add("facets__drawer-tab--active");
		}

		facetTrigger.addEventListener("click", () => {
			// Close nav drawer if open
			document.querySelector('nav-drawer-component')?.close();

			this.querySelector(".facets__container").classList.add("active");
			this.querySelector(".facets__container__overlay").classList.add("active");
			document.body.classList.add("overflow-hidden");
		});

		if (closeButtons.length > 0) {
			closeButtons.forEach((button) => {
				button.addEventListener("click", () => {
					this.querySelector(".facets__container").classList.remove("active");
					this.querySelector(".facets__container__overlay").classList.remove(
						"active"
					);
					document.body.classList.remove("overflow-hidden");
				});
			});
		}
	}

	static setListeners() {
		const onHistoryChange = (event) => {
			const searchParams = event.state
				? event.state.searchParams
				: FacetFiltersForm.searchParamsInitial;
			if (searchParams === FacetFiltersForm.searchParamsPrev) return;
			FacetFiltersForm.renderPage(searchParams, null, false);

			// Sync desktop sort dropdown to the sort_by value from the URL state
			const sortByValue = new URLSearchParams(searchParams).get("sort_by");
			if (sortByValue) {
				document.querySelectorAll(".facet-sort-select").forEach((wrapper) => {
					const input = wrapper.querySelector(".facet-sort-select__input");
					const current = wrapper.querySelector(".facet-sort-select__current");
					const options = wrapper.querySelectorAll(".facet-sort-select__option");
					const matchingOpt = Array.from(options).find((o) => o.dataset.value === sortByValue);
					if (input) input.value = sortByValue;
					options.forEach((opt) => {
						opt.classList.toggle("facet-sort-select__option--selected", opt.dataset.value === sortByValue);
					});
				});
				document.querySelectorAll(".facets__sort-radio-input").forEach((r) => {
					r.checked = r.value === sortByValue;
				});
			}
		};
		window.addEventListener("popstate", onHistoryChange);
	}

	static toggleActiveFacets(disable = true) {
		document.querySelectorAll(".js-facet-remove").forEach((element) => {
			element.classList.toggle("disabled", disable);
		});
	}

	static beginInteraction() {
		// A new choice supersedes any pending filter response immediately, even
		// while the submit debounce is still waiting to run.
		FacetFiltersForm.latestInteractionId += 1;
		FacetFiltersForm.requestController?.abort();
		FacetFiltersForm.requestController = null;
	}

	static startTrace(type, target) {
		FacetFiltersForm.beginInteraction();
		FacetFiltersForm.traceStart = performance.now();
		console.log("[facets] interaction", { type, target });
		if (FacetFiltersForm.traceObserverStarted || !window.PerformanceObserver) return;
		FacetFiltersForm.traceObserverStarted = true;
		new PerformanceObserver((list) => {
			list.getEntries().forEach((entry) => {
				console.log("[facets] long task", JSON.stringify({
					duration: entry.duration,
					startedAt: entry.startTime,
					attribution: entry.attribution?.map((item) => ({
						name: item.name,
						containerType: item.containerType,
						containerSrc: item.containerSrc,
						containerId: item.containerId,
					})),
				}));
			});
		}).observe({ type: "longtask" });
	}

	static trace(label, startedAt) {
		console.log(`[facets] ${label}`, {
			duration: performance.now() - startedAt,
			sinceInteraction: FacetFiltersForm.traceStart
				? performance.now() - FacetFiltersForm.traceStart
				: undefined,
		});
	}

	static renderPage(searchParams, event, updateURLHash = true) {
		const renderId = ++FacetFiltersForm.latestRenderId;
		const interactionId = FacetFiltersForm.latestInteractionId;
		FacetFiltersForm.searchParamsPrev = searchParams;
		const sections = FacetFiltersForm.getSections();
		const countContainer = document.getElementById("ProductCount");
		const countContainerDesktop = document.getElementById(
			"ProductCountDesktop"
		);
		document
			.getElementById("ProductGridContainer")
			.querySelector(".collection")
			.classList.add("loading");
		if (countContainer) {
			countContainer.classList.add("loading");
		}
		if (countContainerDesktop) {
			countContainerDesktop.classList.add("loading");
		}

		sections.forEach((section) => {
			const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
			const filterDataUrl = (element) => element.url === url;

			FacetFiltersForm.filterData.some(filterDataUrl)
				? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, renderId)
				: FacetFiltersForm.renderSectionFromFetch(
						url,
						event,
						renderId,
						interactionId
					);
		});

		document.querySelector("facet-filters-form").bindFacetTriggers();

		if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
	}

	static renderSectionFromFetch(url, event, renderId, interactionId) {
		const fetchStartedAt = performance.now();
		const controller = new AbortController();
		FacetFiltersForm.requestController?.abort();
		FacetFiltersForm.requestController = controller;

		fetch(url, { signal: controller.signal })
			.then((response) => response.text())
			.then((responseText) => {
				const html = responseText;
				FacetFiltersForm.filterData = [
					...FacetFiltersForm.filterData,
					{ html, url },
				];

				// Several requests can be in flight when filters are clicked in
				// quick succession. Never let a slower, older response overwrite
				// the pills and checkbox state from the newest response.
				if (
					renderId !== FacetFiltersForm.latestRenderId ||
					interactionId !== FacetFiltersForm.latestInteractionId
				)
					return;

				FacetFiltersForm.trace("fetch complete", fetchStartedAt);
				let startedAt = performance.now();
				FacetFiltersForm.renderFilters(html, event);
				FacetFiltersForm.trace("filters rendered", startedAt);
				startedAt = performance.now();
				FacetFiltersForm.renderProductGridContainer(html);
				FacetFiltersForm.trace("product grid rendered", startedAt);
				startedAt = performance.now();
				FacetFiltersForm.renderProductCount(html);
				FacetFiltersForm.trace("product count rendered", startedAt);

				window.initializeProductCardSliders?.(
					document.getElementById("ProductGridContainer")
				);
			})
			.catch((error) => {
				if (error.name !== "AbortError") console.error("[facets] fetch failed", error);
			})
			.finally(() => {
				if (FacetFiltersForm.requestController === controller) {
					FacetFiltersForm.requestController = null;
				}
			});
	}

	static renderSectionFromCache(filterDataUrl, event, renderId) {
		if (renderId !== FacetFiltersForm.latestRenderId) return;
		const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
		FacetFiltersForm.renderFilters(html, event);
		FacetFiltersForm.renderProductGridContainer(html);
		FacetFiltersForm.renderProductCount(html);
		window.initializeProductCardSliders?.(
			document.getElementById("ProductGridContainer")
		);
	}

	static renderProductGridContainer(html) {
		document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
			.parseFromString(html, "text/html")
			.getElementById("ProductGridContainer").innerHTML;
		if (
			document.querySelector(".js-load-more") ||
			document.querySelector(".js-infinite-scroll")
		)
			loadMore();

		colorSwatches();
	}

	static renderProductCount(html) {
		const count = new DOMParser()
			.parseFromString(html, "text/html")
			.getElementById("ProductCount").innerHTML;
		const container = document.getElementById("ProductCount");
		const containerDesktop = document.getElementById("ProductCountDesktop");
		container.innerHTML = count;
		container.classList.remove("loading");
		if (containerDesktop) {
			containerDesktop.innerHTML = count;
			containerDesktop.classList.remove("loading");
		}
	}

	static renderFilters(html, event) {
		const parsedHTML = new DOMParser().parseFromString(html, "text/html");

		const facetDetailsElements = parsedHTML.querySelectorAll(
			"#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
		);
		const matchesIndex = (element) => {
			const jsFilter = event ? event.target.closest(".js-filter") : undefined;
			return jsFilter
				? element.dataset.index === jsFilter.dataset.index
				: false;
		};
		const facetsToRender = Array.from(facetDetailsElements).filter(
			(element) => !matchesIndex(element)
		);
		const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

		facetsToRender.forEach((element) => {
			document.querySelector(
				`.js-filter[data-index="${element.dataset.index}"]`
			).innerHTML = element.innerHTML;
		});

		FacetFiltersForm.renderActiveFacets(parsedHTML);
		FacetFiltersForm.renderAdditionalElements(parsedHTML);

		document.querySelector("facet-filters-form").bindFacetTriggers();
		if (countsToRender)
			FacetFiltersForm.renderCounts(
				countsToRender,
				event.target.closest(".js-filter")
			);

		document.querySelectorAll(".facets__reset").forEach((btn) => {
			btn.classList.remove("disabled");
		});
	}

	static renderActiveFacets(html) {
		const activeFacetElementSelectors = [
			".active-facets-mobile",
			".active-facets-desktop",
		];

		activeFacetElementSelectors.forEach((selector) => {
			const activeFacetsElement = html.querySelector(selector);
			if (!activeFacetsElement) return;
			document.querySelector(selector).innerHTML =
				activeFacetsElement.innerHTML;
		});

		FacetFiltersForm.toggleActiveFacets(false);
	}

	static renderAdditionalElements(html) {
		const mobileElementSelectors = [
			".mobile-facets__open",
			".mobile-facets__count",
			//".sorting",
			".facets__selected",
		];

		mobileElementSelectors.forEach((selector) => {
			if (!html.querySelector(selector)) return;
			document.querySelector(selector).innerHTML =
				html.querySelector(selector).innerHTML;
		});

		//document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
	}

	static renderCounts(source, target) {
		const targetElement = target.querySelector(".facets__selected");
		const sourceElement = source.querySelector(".facets__selected");

		const targetElementAccessibility = target.querySelector(".facets__summary");
		const sourceElementAccessibility = source.querySelector(".facets__summary");

		if (sourceElement && targetElement) {
			target.querySelector(".facets__selected").outerHTML =
				source.querySelector(".facets__selected").outerHTML;
		}

		if (targetElementAccessibility && sourceElementAccessibility) {
			target.querySelector(".facets__summary").outerHTML =
				source.querySelector(".facets__summary").outerHTML;
		}
	}

	static updateURLHash(searchParams) {
		history.pushState(
			{ searchParams },
			"",
			`${window.location.pathname}${searchParams && "?".concat(searchParams)}`
		);
	}

	static getSections() {
		return [
			{
				section: document.getElementById("product-grid").dataset.id,
			},
		];
	}

	createSearchParams(form) {
		const formData = new FormData(form);
		const params = new URLSearchParams(formData);
		const deduped = new URLSearchParams();
		for (const key of new Set(params.keys())) {
			const values = params.getAll(key).filter((v) => v !== "");
			if (values.length === 0) continue;
			values.forEach((v) => deduped.append(key, v));
		}
		return deduped.toString();
	}

	onSubmitForm(searchParams, event) {
		FacetFiltersForm.renderPage(searchParams, event);
	}

	onSubmitHandler(event) {
		event.preventDefault();
		const sortFilterForms = document.querySelectorAll(
			"facet-filters-form form"
		);
		if (event.srcElement.className == "mobile-facets__checkbox") {
			const searchParams = this.createSearchParams(
				event.target.closest("form")
			);
			this.onSubmitForm(searchParams, event);
		} else {
			const forms = [];
			const isMobile =
				event.target.closest("form").id === "FacetFiltersFormMobile";

			sortFilterForms.forEach((form) => {
				if (!isMobile) {
					if (
						form.id === "FacetSortForm" ||
						form.id === "FacetFiltersForm" ||
						form.id === "FacetSortDrawerForm"
					) {
						const noJsElements = document.querySelectorAll(".no-js-list");
						noJsElements.forEach((el) => el.remove());
						forms.push(this.createSearchParams(form));
					}
				} else if (form.id === "FacetFiltersFormMobile") {
					forms.push(this.createSearchParams(form));

					// change for desktop custom select
					const desktopCustomSelect =
						document.querySelector(".facet-sort-select");
					if (desktopCustomSelect) {
						const hiddenInput = desktopCustomSelect.querySelector(
							".facet-sort-select__input"
						);
						const customOptions = desktopCustomSelect.querySelectorAll(
							".facet-sort-select__option"
						);
						const currentValue = desktopCustomSelect.querySelector(
							".facet-sort-select__current"
						);
						const mobileFormData = new FormData(form);
						const sortByValue = mobileFormData.get("sort_by");

						customOptions.forEach((option) => {
							const value = option.dataset.value;

							if (value === sortByValue) {
								const text = option.textContent;

								hiddenInput.value = value;
								currentValue.textContent = text;
								customOptions.forEach((option) => {
									option.classList.remove(
										"facet-sort-select__option--selected"
									);
								});
								option.classList.add("facet-sort-select__option--selected");
							}
						});
					}
				}
			});
			const merged = new URLSearchParams(forms.join("&"));
			const deduped = new URLSearchParams();
			for (const key of new Set(merged.keys())) {
				const values = merged.getAll(key).filter((v) => v !== "");
				if (values.length === 0) continue;
				// For sort_by (and any single-value key), keep only the last value
				if (key === "sort_by") {
					deduped.set(key, values[values.length - 1]);
				} else {
					values.forEach((v) => deduped.append(key, v));
				}
			}
			this.onSubmitForm(deduped.toString(), event);
		}
	}

	onActiveFilterClick(event) {
		event.preventDefault();
		FacetFiltersForm.startTrace("active facet removal", "pill");
		FacetFiltersForm.toggleActiveFacets();
		const url =
			event.currentTarget.href.indexOf("?") == -1
				? ""
				: event.currentTarget.href.slice(
						event.currentTarget.href.indexOf("?") + 1
				  );
		FacetFiltersForm.renderPage(url);
	}

	handleFacetButtonClick(event) {
		event.preventDefault();

		if (!this.facetButton || !this.facetWrapper || !this.productGrid) return;
		if (this.facetButton.classList.contains("proccess")) return;

		// if active - remove active classes and add full-width class on product grid for vertical facets
		if (this.facetButton.classList.contains("active")) {
			if (this.facetButton.classList.contains("horizontal")) {
				document.body.classList.remove("overflow-hidden-drawer");
				this.facetWrapper.classList.remove("active");
				this.facetButton.classList.remove("active");

				this.facetWrapper.addEventListener("transitionend", () => {
					if (this.facetButton) this.facetButton.focus();
				});
			}

			if (this.facetButton.classList.contains("vertical")) {
				this.facetButton.classList.add("proccess");
				this.productGrid.classList.add("full-width");
				this.facetWrapper.classList.remove("animate");

				if (this.openTimeout) clearTimeout(this.openTimeout);
				this.openTimeout = setTimeout(() => {
					this.facetWrapper.classList.remove("active");
					this.facetButton.classList.remove("active");
					this.facetButton.classList.remove("proccess");
				}, 100);
			}
		}
		// if no active - add active classes and remove full-width class on product grid for vertical facets
		else {
			if (this.facetButton.classList.contains("horizontal")) {
				document.body.classList.add("overflow-hidden-drawer");
				this.facetWrapper.classList.add("active");
				this.facetButton.classList.add("active");

				this.facetWrapper.addEventListener("transitionend", () => {
					if (this.closeButton) this.closeButton.focus();
				});
			}

			if (this.facetButton.classList.contains("vertical")) {
				this.facetButton.classList.add("proccess");
				this.productGrid.classList.remove("full-width");
				this.facetWrapper.classList.add("active");

				if (this.closeTimeout) clearTimeout(this.closeTimeout);
				this.closeTimeout = setTimeout(() => {
					this.facetWrapper.classList.add("animate");
					this.facetButton.classList.add("active");
					this.facetButton.classList.remove("proccess");
				}, 100);
			}
		}
	}

	closeFacets(event) {
		event.preventDefault();
		this.facetButton?.classList.remove("active");
		this.facetWrapper?.classList.remove("active");
		document.body?.classList.remove("overflow-hidden-drawer");
	}

	onHandleKeyDown(event) {
		if (
			event.key === "Escape" &&
			this.facetWrapper?.classList.contains("active")
		) {
			this.closeFacets(event);
		}
	}

	initCustomSortSelect() {
		const allWrappers = this.querySelectorAll(".facet-sort-select");

		const syncAll = (value, text) => {
			allWrappers.forEach((wrapper) => {
				const input = wrapper.querySelector(".facet-sort-select__input");
				const current = wrapper.querySelector(".facet-sort-select__current");
				const options = wrapper.querySelectorAll(".facet-sort-select__option");
				if (input) input.value = value;
				if (current) current.textContent = text.trim();
				options.forEach((opt) => {
					opt.classList.toggle(
						"facet-sort-select__option--selected",
						opt.dataset.value === value
					);
				});
			});
			this.querySelectorAll(".facets__sort-radio-input").forEach((r) => {
				r.checked = r.value === value;
			});
		};

		// When a drawer sort radio is changed, sync the desktop dropdown immediately
		this.querySelectorAll(".facets__sort-radio-input").forEach((radio) => {
			radio.addEventListener("change", (e) => {
				const value = e.target.value;
				const matchingOpt = this.querySelector(`.facet-sort-select__option[data-value="${value}"]`);
				const text = matchingOpt ? matchingOpt.textContent.trim() : value;
				syncAll(value, text);
			});
		});

		allWrappers.forEach((selectWrapper) => {
			const customSelect = selectWrapper.querySelector(
				".facet-sort-select__select"
			);
			const customOptions = selectWrapper.querySelectorAll(
				".facet-sort-select__option"
			);

			customSelect.addEventListener("click", () => {
				selectWrapper.classList.toggle("open");
			});

			customSelect.addEventListener("keydown", (event) => {
				if (event.key === "Enter") {
					selectWrapper.classList.toggle("open");
				}
			});

			const onSelectOption = (event) => {
				event.preventDefault();

				const option = event.target.closest(".facet-sort-select__option");
				if (option && selectWrapper.contains(option)) {
					const value = option.dataset.value;
					const text = option.textContent;

					syncAll(value, text);
					this.facetForm?.dispatchEvent(new Event("input"));
					selectWrapper.classList.remove("open");
				}
			};

			customOptions.forEach((option) => {
				option.addEventListener("click", (event) => onSelectOption(event));

				option.addEventListener("keydown", (event) => {
					if (event.key === "Enter") {
						onSelectOption(event);
					}
				});
			});

			document.addEventListener("click", (event) => {
				if (!selectWrapper.contains(event.target)) {
					selectWrapper.classList.remove("open");
				}
			});
		});
	}
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.latestRenderId = 0;
FacetFiltersForm.latestInteractionId = 0;
FacetFiltersForm.requestController = null;
FacetFiltersForm.traceStart = null;
FacetFiltersForm.traceObserverStarted = false;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
	constructor() {
		super();
		this.querySelectorAll("input").forEach((element) =>
			element.addEventListener("change", this.onRangeChange.bind(this))
		);
		// When the user interacts with this price-range, clear number inputs in all
		// other price-range elements so only this one's values end up in FormData.
		this.querySelectorAll("input").forEach((element) =>
			element.addEventListener("input", () => {
				document.querySelectorAll("price-range").forEach((pr) => {
					if (pr !== this) {
						pr.querySelectorAll(".field__input").forEach((input) => {
							input.value = "";
						});
					}
				});
			})
		);
		this.setMinAndMaxValues();
		this.controlSlider();
		this.controlInput();
	}

	onRangeChange(event) {
		this.adjustToValidValues(event.currentTarget);
		this.setMinAndMaxValues();
	}

	setMinAndMaxValues() {
		const inputs = this.querySelectorAll(".field__input");
		const minInput = inputs[0];
		const maxInput = inputs[1];
		if (maxInput.value) minInput.setAttribute("max", maxInput.value);
		if (minInput.value) maxInput.setAttribute("min", minInput.value);
		if (minInput.value === "") {
			maxInput.setAttribute("min", 0);
		}
		if (maxInput.value === "") {
			minInput.setAttribute("max", maxInput.getAttribute("max"));
		}
	}

	adjustToValidValues(input) {
		const value = Number(input.value);
		const min = Number(input.getAttribute("min"));
		const max = Number(input.getAttribute("max"));

		if (value < min) input.value = min;
		if (value > max) input.value = max;
	}

	fillSlider() {
		const inputRangeWrappers = document.querySelectorAll(
			".facets__price .facets__range"
		);
		inputRangeWrappers.forEach((inputWrapper) => {
			const inputsRange = inputWrapper.querySelectorAll(".field__range");
			const inputRangeFrom = inputsRange[0];
			const inputRangeTo = inputsRange[1];

			const range = Number(inputRangeTo.max) - Number(inputRangeTo.min);
			const fromCurrent =
				Number(inputRangeFrom.value) - Number(inputRangeTo.min);
			const toCurrent = Number(inputRangeTo.value) - Number(inputRangeTo.min);
			const minRange = (fromCurrent / range) * 100;
			const maxRange = (toCurrent / range) * 100;

			inputWrapper.setAttribute(
				"style",
				`--range-min: ${minRange}%; --range-max: ${maxRange}%`
			);
		});
	}

	controlSlider() {
		const inputRangeWrappers = document.querySelectorAll(
			".facets__price .facets__range"
		);
		const inputNumberWrappers = document.querySelectorAll(
			".facets__price .facets__price-wrapper"
		);

		inputRangeWrappers.forEach((inputWrapper, index) => {
			const inputsRange = inputWrapper.querySelectorAll(".field__range");
			const inputRangeFrom = inputsRange[0];
			const inputRangeTo = inputsRange[1];
			const inputNumberFrom =
				inputNumberWrappers[index].querySelectorAll(".field__input")[0];
			const inputNumberTo =
				inputNumberWrappers[index].querySelectorAll(".field__input")[1];

			inputRangeFrom.oninput = () => {
				const from = Number(inputRangeFrom.value);
				const to = Number(inputRangeTo.value);
				if (from > to) {
					inputRangeFrom.value = to;
					inputNumberFrom.value = to;
				} else {
					inputNumberFrom.value = from;
				}

				this.fillSlider();
			};

			if (Number(inputRangeTo.value) <= 0) {
				inputRangeTo.style.zIndex = 2;
			} else {
				inputRangeTo.style.zIndex = 0;
			}

			inputRangeTo.oninput = () => {
				const from = Number(inputRangeFrom.value);
				const to = Number(inputRangeTo.value);
				if (from <= to) {
					inputRangeTo.value = to;
					inputNumberTo.value = to;
				} else {
					inputNumberTo.value = from;
					inputRangeTo.value = from;
				}

				if (Number(inputRangeTo.value) <= 0) {
					inputRangeTo.style.zIndex = 2;
				} else {
					inputRangeTo.style.zIndex = 0;
				}

				this.fillSlider();
			};
		});
	}

	controlInput() {
		const inputRangeWrappers = document.querySelectorAll(
			".facets__price .facets__range"
		);
		const inputNumberWrappers = document.querySelectorAll(
			".facets__price .facets__price-wrapper"
		);

		inputNumberWrappers.forEach((inputWrapper, index) => {
			const inputsNumber = inputWrapper.querySelectorAll(".field__input");
			const inputNumberFrom = inputsNumber[0];
			const inputNumberTo = inputsNumber[1];
			const inputRangeFrom =
				inputRangeWrappers[index].querySelectorAll(".field__range")[0];
			const inputRangeTo =
				inputRangeWrappers[index].querySelectorAll(".field__range")[1];

			inputNumberFrom.oninput = () => {
				const from = Number(inputNumberFrom.value);
				const to = Number(inputNumberTo.value);

				if (to && from > to) {
					inputRangeFrom.value = to;
				} else {
					inputRangeFrom.value = from;
				}

				this.fillSlider();
			};

			inputNumberTo.oninput = () => {
				const from = Number(inputNumberFrom.value);
				const to = Number(inputNumberTo.value);

				if (to >= from) {
					inputRangeTo.value = to;
				} else {
					inputRangeTo.value = from;
				}

				this.fillSlider();
			};

			inputNumberFrom.onblur = () => {
				const from = Number(inputNumberFrom.value);
				const min = Number(inputNumberFrom.getAttribute("min"));
				const max = Number(inputNumberFrom.getAttribute("max"));

				if (from < min) {
					inputRangeFrom.value = min;
					inputNumberFrom.value = min;
				} else {
					inputRangeFrom.value = from;
				}

				this.fillSlider();
			};

			inputNumberTo.onblur = () => {
				const to = Number(inputNumberTo.value);
				const min = Number(inputNumberTo.getAttribute("min"));
				const max = Number(inputNumberTo.getAttribute("max"));

				if (to > max) {
					inputRangeTo.value = max;
					inputNumberTo.value = max;
				} else {
					inputRangeTo.value = to;
				}

				this.fillSlider();
			};
		});
	}
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
	constructor() {
		super();
		const facetLink = this.querySelector("a");
		facetLink.setAttribute("role", "button");
		facetLink.addEventListener("click", this.closeFilter.bind(this));
		facetLink.addEventListener("keyup", (event) => {
			event.preventDefault();
			if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
		});
	}

	closeFilter(event) {
		event.preventDefault();
		const form =
			this.closest("facet-filters-form") ||
			document.querySelector("facet-filters-form");
		form.onActiveFilterClick(event);
	}
}

customElements.define("facet-remove", FacetRemove);
